import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import ProgramSelect from '@/components/ProgramSelect';
import StaffSelect from '@/components/select/StaffSelect/StaffSelect';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import {
  PersonEnrollmentFormPayload,
  PersonEnrollmentFormValues,
  Props
} from '@/forms/EnrollmentForm/types';
import FieldError from '@/forms/FieldError';
import PersonContext from '@/pages/Person/PersonContext';
import ProgramRepository from '@/repositories/ProgramRepository';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import DatetimePicker from '@/ui/components/Form/DatetimePicker';
import InputGroup from '@/ui/components/Form/InputGroup';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const schema = yup.object().shape({
  admitted_at: yup.string().required('Admitted At is required'),
  program_id: yup.string().required('Program is required'),
  case_manager_id: yup.object().shape({
    label: yup.string().default(''),
    value: yup.string().default('')
  }),
  primary_counselor_id: yup.object().shape({
    label: yup.string().default(''),
    value: yup.string().default('')
  }),
  primary_substance: yup.string().required('Primary Concern is required')
});

const EnrollmentForm = ({ episodeOfCare, onSuccess, personId, programQualificationId }: Props) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const {
    person: { program_qualification }
  } = useContext(PersonContext);
  const programRepository = ProgramRepository();

  const determineInitialAdmittedAt = () => {
    if (
      program_qualification?.intake_appointment_attendance &&
      program_qualification.intake_appointment_attendance.appointment_start_at
    ) {
      return program_qualification.intake_appointment_attendance.appointment_start_at;
    }

    return new Date().toISOString();
  };

  const initialValues: PersonEnrollmentFormValues = {
    admitted_at: determineInitialAdmittedAt(),
    case_manager_id: emptySelectOption,
    primary_substance: '',
    program_id: '',
    primary_counselor_id: emptySelectOption
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch
  } = useForm({ defaultValues: initialValues, resolver: yupResolver(schema) });
  const values = watch();

  const enroll = useMutation({
    mutationFn: ({ values }: { values: PersonEnrollmentFormPayload }) => {
      return programRepository.enrollPerson({ params: values, programQualificationId });
    },
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Successfully enrolled person' });
      personId && (await queryClient.invalidateQueries({ queryKey: ['getPerson', personId] }));
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: PersonEnrollmentFormValues) => {
    const payload: PersonEnrollmentFormPayload = {
      ...values,
      case_manager_id: values.case_manager_id.value ? values.case_manager_id.value : null,
      primary_counselor_id: values.primary_counselor_id.value
        ? values.primary_counselor_id.value
        : null,
      program_id: values.program_id
    };

    enroll.mutate({ values: payload });
  };

  const onPrimarySubstanceChange = useCallback(
    (primarySubstance: string | null) => setValue('primary_substance', primarySubstance ?? ''),
    [setValue]
  );

  const onProgramChange = useCallback(
    (program: string | null) => setValue('program_id', program ?? ''),
    [setValue]
  );

  register('primary_substance', {
    required: 'Primary Concern is required'
  });

  return (
    <div>
      <Heading value={3} text="Enroll Person" />

      {episodeOfCare && !episodeOfCare.discharged_at && (
        <Alert
          level="error"
          message="This person is already enrolled in a program. Please edit their current program."
        />
      )}

      <form onSubmit={handleSubmit(submitForm)} data-testid="enrollment-form">
        <InputGroup>
          <FieldError message={errors.primary_substance?.message} />
          <Controller
            name="program_id"
            control={control}
            render={() => {
              return (
                <ProgramSelect
                  onPrimarySubstanceChange={onPrimarySubstanceChange}
                  onProgramChange={onProgramChange}
                />
              );
            }}
            rules={{
              validate: () => !values.program_id
            }}
          />

          <FieldError message={errors.program_id?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            name="admitted_at"
            control={control}
            render={({ field }) => {
              return (
                <DatetimePicker
                  label="Admitted At"
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(val) => field.onChange(val ? val.toISOString() : val)}
                  showTimeSelect={true}
                  value={field.value}
                />
              );
            }}
            rules={{ required: 'Admitted At is required' }}
          />

          <FieldError message={errors.admitted_at?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            name="primary_counselor_id"
            control={control}
            render={({ field }) => {
              return (
                <StaffSelect
                  label="Primary Counselor"
                  onChange={field.onChange}
                  placeholder="Select Primary Counselor"
                  selectedOption={
                    field.value ? (field.value as OptionType<string>) : emptySelectOption
                  }
                />
              );
            }}
          />
          <FieldError message={errors.primary_counselor_id?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            name="case_manager_id"
            control={control}
            render={({ field }) => {
              return (
                <StaffSelect
                  label="Case Manager"
                  onChange={field.onChange}
                  placeholder="Select Case Manager"
                  selectedOption={
                    field.value ? (field.value as OptionType<string>) : emptySelectOption
                  }
                />
              );
            }}
          />
          <FieldError message={errors.case_manager_id?.message} />
        </InputGroup>

        <InputGroup>
          <Button disabled={enroll.isPending} type="submit">
            Submit
          </Button>
        </InputGroup>
      </form>
    </div>
  );
};

export default EnrollmentForm;
